import React, { useEffect, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { GlobeIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon, BarChart2Icon, LinkIcon, XIcon, LockIcon, ServerIcon, DatabaseIcon, CreditCardIcon, UsersIcon, FileTextIcon, WalletIcon, ReceiptIcon, BanknoteIcon, SettingsIcon, CheckIcon, CopyIcon, PlusIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
interface OAuth2App {
  id: string;
  name: string;
  logo: string;
  publicKey: string;
  privateKey: string;
  scopes: string[];
  status: 'active' | 'inactive';
  transactionVolume: number;
  transactionCount: number;
  domains: string[];
  connectedDate: string;
  lastUsed: string;
  expiresAt?: string | null;
}
type ExpirationPreset = 'never' | '30d' | '60d' | '90d' | '180d' | '1y' | 'custom';
const EXPIRATION_PRESETS: Array<{
  value: ExpirationPreset;
  label: string;
}> = [{
  value: 'never',
  label: 'Never expires'
}, {
  value: '30d',
  label: '30 days'
}, {
  value: '60d',
  label: '60 days'
}, {
  value: '90d',
  label: '90 days'
}, {
  value: '180d',
  label: '6 months'
}, {
  value: '1y',
  label: '1 year'
}, {
  value: 'custom',
  label: 'Custom date…'
}];
function presetToIsoDate(preset: ExpirationPreset, customDate: string): string | null {
  if (preset === 'never') return null;
  if (preset === 'custom') return customDate || null;
  const days: Record<Exclude<ExpirationPreset, 'never' | 'custom'>, number> = {
    '30d': 30,
    '60d': 60,
    '90d': 90,
    '180d': 180,
    '1y': 365
  };
  const d = new Date();
  d.setDate(d.getDate() + days[preset as Exclude<ExpirationPreset, 'never' | 'custom'>]);
  return d.toISOString().split('T')[0];
}
function formatExpiration(iso: string): {
  label: string;
  tone: 'normal' | 'soon' | 'expired';
} {
  const target = new Date(iso);
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.ceil((target.getTime() - now.getTime()) / msPerDay);
  const formatted = target.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  if (diffDays < 0) return {
    label: `Expired ${formatted}`,
    tone: 'expired'
  };
  if (diffDays <= 14) return {
    label: `Expires ${formatted} (${diffDays}d)`,
    tone: 'soon'
  };
  return {
    label: `Expires ${formatted}`,
    tone: 'normal'
  };
}
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
const scopeConfig: Record<string, {
  name: string;
  icon: React.ReactNode;
}> = {
  transactions: {
    name: 'Transactions',
    icon: <CreditCardIcon size={12} className="text-[#9F140F]" data-id="element-13724" />
  },
  refunds: {
    name: 'Refunds',
    icon: <RefreshCwIcon size={12} className="text-yellow-700" aria-hidden="true" data-id="element-13725" />
  },
  company_info: {
    name: 'Company Info',
    icon: <UsersIcon size={12} className="text-green-600" data-id="element-13726" />
  },
  customer_data: {
    name: 'Customer Data',
    icon: <DatabaseIcon size={12} className="text-purple-600" data-id="element-13727" />
  },
  suppliers: {
    name: 'Suppliers',
    icon: <ServerIcon size={12} className="text-blue-500" data-id="element-13728" />
  },
  bills: {
    name: 'Bills',
    icon: <FileTextIcon size={12} className="text-rose-500" data-id="element-13729" />
  },
  invoices: {
    name: 'Invoices',
    icon: <ReceiptIcon size={12} className="text-teal-600" data-id="element-13730" />
  },
  balance: {
    name: 'Balance',
    icon: <WalletIcon size={12} className="text-emerald-600" data-id="element-13731" />
  },
  settlements: {
    name: 'Settlements',
    icon: <BanknoteIcon size={12} className="text-indigo-500" data-id="element-13732" />
  },
  analytics: {
    name: 'Analytics',
    icon: <BarChart2Icon size={12} className="text-amber-500" data-id="element-13733" />
  },
  settings: {
    name: 'Settings',
    icon: <SettingsIcon size={12} className="text-gray-500" data-id="element-13734" />
  }
};
const defaultApps: OAuth2App[] = [{
  id: 'oauth-001',
  name: 'Shopify',
  logo: 'https://cdn.shopify.com/s/files/1/0533/2089/files/Shopify_logo_small.png?v=1512152678',
  publicKey: 'pub_key_6a8b4c2d3e7f9g0h1i2j',
  privateKey: 'prv_key_5f4e3d2c1b0a9z8y7x6w5v4u3t2s1r',
  scopes: ['transactions', 'refunds', 'company_info'],
  status: 'active',
  transactionVolume: 87500,
  transactionCount: 1243,
  domains: ['mystore.shopify.com', 'checkout.shopify.com'],
  connectedDate: '2023-05-12',
  lastUsed: '2 hours ago'
}, {
  id: 'oauth-002',
  name: 'Opera PMS',
  logo: 'https://www.oracle.com/a/ocom/img/cb71-opera-cloud-services.jpg',
  publicKey: 'pub_key_7b9c5d3e1f8g2h4i6j0k',
  privateKey: 'prv_key_6g5f4e3d2c1b0a9z8y7x6w5v4u3t',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 125400,
  transactionCount: 2156,
  domains: ['operahotel.com', 'api.operacloud.com'],
  connectedDate: '2023-02-28',
  lastUsed: '5 minutes ago'
}, {
  id: 'oauth-003',
  name: 'CloudBeds',
  logo: 'https://assets.cloudbeds.com/static/images/cloudbeds-logo.svg',
  publicKey: 'pub_key_8c0d6e4f2g9h1i3j5k7l',
  privateKey: 'prv_key_7h6g5f4e3d2c1b0a9z8y7x6w5v',
  scopes: ['transactions', 'refunds', 'customer_data'],
  status: 'active',
  transactionVolume: 65800,
  transactionCount: 954,
  domains: ['app.cloudbeds.com', 'api.cloudbeds.com'],
  connectedDate: '2023-08-15',
  lastUsed: '1 day ago'
}, {
  id: 'oauth-004',
  name: 'Mews Systems',
  logo: 'https://mews-careers.cdn.prismic.io/mews-careers/a84d4ff0-6b90-44a2-9f7e-c5b511ef17d2_mews-logo.svg',
  publicKey: 'pub_key_9d1e7f5g3h0i2j4k6l8m',
  privateKey: 'prv_key_8i7h6g5f4e3d2c1b0a9z8y7x6w',
  scopes: ['transactions', 'company_info', 'settlements'],
  status: 'inactive',
  transactionVolume: 32400,
  transactionCount: 567,
  domains: ['mews.com', 'api.mews.com'],
  connectedDate: '2023-04-10',
  lastUsed: '3 weeks ago'
}, {
  id: 'oauth-006',
  name: 'Salesforce',
  logo: 'https://www.salesforce.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg',
  publicKey: 'pub_key_1f3g5h7i9j0k2l4m6n8o',
  privateKey: 'prv_key_0k9j8i7h6g5f4e3d2c1b0a9z8y',
  scopes: ['company_info', 'customer_data'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['salesforce.com', 'force.com'],
  connectedDate: '2023-09-05',
  lastUsed: '3 days ago'
}, {
  id: 'oauth-008',
  name: 'Xero Accounting',
  logo: 'https://www.xero.com/content/dam/xero/pilot-images/brand/logos/logo-xero-blue.svg',
  publicKey: 'pub_key_3h5i7j9k1l2m4n6o8p0q',
  privateKey: 'prv_key_2m1l0k9j8i7h6g5f4e3d2c1b0a',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['xero.com', 'api.xero.com'],
  connectedDate: '2023-07-22',
  lastUsed: '12 hours ago'
}];
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-13735">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-13736">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-13737" />}
        <span className="truncate" data-id="element-13738">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-13739" />
      </button>

      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-13740">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#E2231A]/5 font-medium' : 'text-gray-700'}`} data-id="element-13741">
              <div className="flex items-center gap-2" data-id="element-13742">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-13743" />}
                <span data-id="element-13744">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#9F140F] flex-shrink-0" data-id="element-13745" />}
            </button>)}
        </div>}
    </div>;
}
const getStatusBadge = (status: 'active' | 'inactive') => {
  const styles = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-red-50 text-red-700'
  };
  const icons = {
    active: <CheckCircleIcon size={11} className="mr-1" data-id="element-13746" />,
    inactive: <XCircleIcon size={11} className="mr-1" data-id="element-13747" />
  };
  const labels = {
    active: 'Active',
    inactive: 'Inactive'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-13748">
      {icons[status]}
      {labels[status]}
    </span>;
};
function generateKey(prefix: string) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let key = `${prefix}_`;
  for (let i = 0; i < 24; i++) key += chars[Math.floor(Math.random() * chars.length)];
  return key;
}
export function Connect() {
  const {
    t
  } = useTranslation('terminals');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPrivateKeys, setShowPrivateKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [apps, setApps] = useState<OAuth2App[]>(defaultApps);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [newExpirationPreset, setNewExpirationPreset] = useState<ExpirationPreset>('never');
  const [newCustomExpirationDate, setNewCustomExpirationDate] = useState('');
  const [editingApp, setEditingApp] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomains, setEditDomains] = useState('');
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const togglePrivateKey = (appId: string) => {
    setShowPrivateKeys(prev => ({
      ...prev,
      [appId]: !prev[appId]
    }));
  };
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 1500);
  };
  const requestToggle = (appId: string) => {
    setPendingToggle(appId);
  };
  const confirmToggle = () => {
    if (!pendingToggle) return;
    setApps(prev => prev.map(app => app.id === pendingToggle ? {
      ...app,
      status: app.status === 'active' ? 'inactive' : 'active'
    } : app));
    setPendingToggle(null);
  };
  const pendingApp = pendingToggle ? apps.find(a => a.id === pendingToggle) : null;
  const openEditModal = (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;
    setEditingApp(appId);
    setEditName(app.name);
    setEditDomains(app.domains.join(', '));
    setEditScopes([...app.scopes]);
  };
  const toggleEditScope = (scopeId: string) => {
    setEditScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleSaveEdit = () => {
    if (!editingApp || !editName.trim()) return;
    const domains = editDomains.split(',').map(d => d.trim()).filter(Boolean);
    setApps(prev => prev.map(app => app.id === editingApp ? {
      ...app,
      name: editName.trim(),
      domains: domains.length > 0 ? domains : app.domains,
      scopes: editScopes.length > 0 ? editScopes : app.scopes
    } : app));
    setEditingApp(null);
  };
  const toggleScope = (scopeId: string) => {
    setNewScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleAddApp = () => {
    if (!newName.trim()) return;
    const domains = newDomains.split(',').map(d => d.trim()).filter(Boolean);
    const expiresAt = presetToIsoDate(newExpirationPreset, newCustomExpirationDate);
    const newApp: OAuth2App = {
      id: `oauth-${Date.now()}`,
      name: newName.trim(),
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName.trim())}&background=E2231A&color=fff&size=32`,
      publicKey: generateKey('pub_key'),
      privateKey: generateKey('prv_key'),
      scopes: newScopes.length > 0 ? newScopes : ['transactions'],
      status: 'active',
      transactionVolume: 0,
      transactionCount: 0,
      domains: domains.length > 0 ? domains : [`${newName.trim().toLowerCase().replace(/\s+/g, '')}.com`],
      connectedDate: new Date().toISOString().split('T')[0],
      lastUsed: 'Just now',
      expiresAt
    };
    setApps(prev => [newApp, ...prev]);
    setNewName('');
    setNewDomains('');
    setNewScopes([]);
    setNewExpirationPreset('never');
    setNewCustomExpirationDate('');
    setShowAddModal(false);
  };
  const filteredApps = apps.filter(app => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = app.name.toLowerCase().includes(q) || app.publicKey.includes(searchQuery) || app.domains.some(d => d.toLowerCase().includes(q));
    return matchesSearch && (statusFilter === 'all' || app.status === statusFilter);
  });
  const totalConnections = apps.length;
  const activeConnections = apps.filter(a => a.status === 'active').length;
  const totalVolume = apps.reduce((s, a) => s + a.transactionVolume, 0);
  return <div className="space-y-5" data-id="element-13749">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-13750">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-13751">
          <div className="h-10 w-10 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-13752">
            <GlobeIcon size={18} className="text-[#9F140F]" data-id="element-13753" />
          </div>
          <div data-id="element-13754">
            <p className="text-xs text-gray-500" data-id="element-13755">
              {t('pages.Connect.total_connections')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-13756">
              {totalConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-13757">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-13758">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-13759" />
          </div>
          <div data-id="element-13760">
            <p className="text-xs text-gray-500" data-id="element-13761">
              {t('pages.Connect.active_connections')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-13762">
              {activeConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-13763">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-13764">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-13765" />
          </div>
          <div data-id="element-13766">
            <p className="text-xs text-gray-500" data-id="element-13767">
              {t('pages.Connect.total_volume')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-13768">
              {getTenant().currency.code} {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-13769">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-13770">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-13771">
            <GlobeIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-13772" />
            {t('pages.Connect.oauth2_connections')}
          </h2>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5" data-id="element-13773">
            <PlusIcon size={14} data-id="element-13774" />
            {t('pages.Connect.add_connection')}
          </button>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-13775">
          <div className="relative flex-1" data-id="element-13776">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-13777" />

            <input type="search" aria-label={t('pages.Connect.attrs.search_by_app_name_api_key')} className={`${inputClass} pl-9`} placeholder={t('pages.Connect.attrs.search_by_app_name_api_key')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-13778" />
          </div>
          <div className="flex items-center gap-2" data-id="element-13779">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'active',
            label: 'Active',
            dot: 'bg-yellow-500'
          }, {
            value: 'inactive',
            label: 'Inactive',
            dot: 'bg-red-500'
          }]} data-id="element-13780" />

            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-13781">
              <RefreshCwIcon size={13} data-id="element-13782" />
              {t('pages.Connect.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── APP GRID ─── */}
      {filteredApps.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-13783">
          {filteredApps.map(app => <div key={app.id} className="bg-white rounded-xl border border-gray-200" data-id="element-13784">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-13785">
                <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-13786">
                  <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-13787">
                    <img src={app.logo} alt={app.name} className="w-full h-full object-contain p-1" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.name}&background=FFC70A&color=fff&size=32`;
              }} data-id="element-13788" />
                  </div>
                  <div className="min-w-0" data-id="element-13789">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-13790">
                      {app.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-13791">
                      {t('pages.Connect.connected')} {app.connectedDate}
                    </p>
                  </div>
                </div>
                <button onClick={() => requestToggle(app.id)} className="relative flex-shrink-0 ml-3" title={app.status === 'active' ? 'Deactivate' : 'Activate'} data-id="element-13792">
                  <div className={`w-9 h-5 rounded-full transition-colors ${app.status === 'active' ? 'bg-yellow-500' : 'bg-gray-300'}`} data-id="element-13793">
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${app.status === 'active' ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-13794" />
                  </div>
                </button>
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-13795">
                {/* Public key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-13796">
                  <div className="flex items-center justify-between mb-1" data-id="element-13797">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-13798">
                      <KeyIcon size={11} className="text-gray-400" data-id="element-13799" />
                      {t('pages.Connect.public_key')}
                    </span>
                    <button onClick={() => copyToClipboard(app.publicKey, `${app.id}-pub`)} aria-label="Copy public key" className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-13800">
                      {copiedKey === `${app.id}-pub` ? <CheckIcon size={12} className="text-green-500" aria-hidden="true" data-id="element-13801" /> : <CopyIcon size={12} aria-hidden="true" data-id="element-13802" />}
                    </button>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-13803">
                    {app.publicKey}
                  </code>
                </div>

                {/* Private key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-13804">
                  <div className="flex items-center justify-between mb-1" data-id="element-13805">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-13806">
                      <LockIcon size={11} className="text-gray-400" data-id="element-13807" />
                      {t('pages.Connect.private_key')}
                    </span>
                    <div className="flex items-center gap-1.5" data-id="element-13808">
                      <button onClick={() => togglePrivateKey(app.id)} aria-label={showPrivateKeys[app.id] ? 'Hide private key' : 'Show private key'} aria-pressed={!!showPrivateKeys[app.id]} className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-13809">
                        {showPrivateKeys[app.id] ? <EyeOffIcon size={12} aria-hidden="true" data-id="element-13810" /> : <EyeIcon size={12} aria-hidden="true" data-id="element-13811" />}
                      </button>
                      <button onClick={() => copyToClipboard(app.privateKey, `${app.id}-prv`)} aria-label="Copy private key" className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-13812">
                        {copiedKey === `${app.id}-prv` ? <CheckIcon size={12} className="text-green-500" aria-hidden="true" data-id="element-13813" /> : <CopyIcon size={12} aria-hidden="true" data-id="element-13814" />}
                      </button>
                    </div>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-13815">
                    {showPrivateKeys[app.id] ? app.privateKey : '••••••••••••••••••••••••••••••'}
                  </code>
                </div>

                {/* Scopes */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-13816">
                  <span className="text-xs text-gray-500 block mb-1.5" data-id="element-13817">
                    {t('pages.Connect.scopes')}
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-13818">
                    {app.scopes.map(s => {
                const cfg = scopeConfig[s];
                if (!cfg) return null;
                return <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[11px] text-gray-700" data-id="element-13819">
                          {cfg.icon}
                          {cfg.name}
                        </span>;
              })}
                  </div>
                </div>

                {/* Domains */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-13820">
                  <span className="text-xs text-gray-500 flex items-center gap-1 mb-1.5" data-id="element-13821">
                    <LinkIcon size={11} className="text-gray-400" data-id="element-13822" />
                    {t('pages.Connect.domains')}
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-13823">
                    {app.domains.map(d => <span key={d} className="inline-flex items-center px-2 py-0.5 bg-[#E2231A]/5 text-[#E2231A] text-[11px] font-medium rounded" data-id="element-13824">
                        {d}
                      </span>)}
                  </div>
                </div>

                {/* Expiration */}
                <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between" data-id="element-13825">
                  <span className="text-xs text-gray-500" data-id="element-13826">Expiration</span>
                  {app.expiresAt ? (() => {
              const {
                label,
                tone
              } = formatExpiration(app.expiresAt);
              const toneClass = tone === 'expired' ? 'text-red-600' : tone === 'soon' ? 'text-amber-600' : 'text-gray-700';
              return <span className={`text-[11px] font-medium ${toneClass}`} data-id="element-13827">
                          {label}
                        </span>;
            })() : <span className="text-[11px] text-gray-400" data-id="element-13828">
                      Never expires
                    </span>}
                </div>

                {/* Volume & count */}
                <div className="grid grid-cols-2" data-id="element-13829">
                  <div className="px-5 py-2.5 border-b border-gray-100 border-r border-gray-100" data-id="element-13830">
                    <span className="text-xs text-gray-500" data-id="element-13831">
                      {t('pages.Connect.volume')}
                    </span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-13832">
                      {getTenant().currency.code}{' '}
                      {app.transactionVolume.toLocaleString()}
                    </p>
                  </div>
                  <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-13833">
                    <span className="text-xs text-gray-500" data-id="element-13834">
                      {t('pages.Connect.transactions')}
                    </span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-13835">
                      {app.transactionCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 flex items-center justify-between" data-id="element-13836">
                <span className="text-[11px] text-gray-400" data-id="element-13837">
                  {t('pages.Connect.last_used')} {app.lastUsed}
                </span>
                <button onClick={() => openEditModal(app.id)} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors flex items-center gap-1" data-id="element-13838">
                  <SettingsIcon size={11} data-id="element-13839" />
                  {t('pages.Connect.settings')}
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-13840">
          <GlobeIcon size={24} className="text-gray-400 mb-3" data-id="element-13841" />
          <p className="text-sm font-medium text-gray-700" data-id="element-13842">
            {t('pages.Connect.no_connections_found')}
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-13843">
            {t('pages.Connect.no_connections_match_your_search_criteria')}
          </p>
        </div>}

      {/* ─── ADD CONNECTION MODAL ─── */}
      {showAddModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowAddModal(false);
    }} data-id="element-13844">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-13845">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-13846">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-13847">
                  {t('pages.Connect.add_connection')}
                </h3>
                <button onClick={() => setShowAddModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-13848">
                  <XIcon size={18} aria-hidden="true" data-id="element-13849" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-13850">
                {/* App name */}
                <div data-id="element-13851">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13852">
                    {t('pages.Connect.app_name')}{' '}
                    <span className="text-red-400" data-id="element-13853">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.eg_opera_pms_salesforce')} value={newName} onChange={e => setNewName(e.target.value)} autoFocus data-id="element-13854" />
                </div>

                {/* Domains */}
                <div data-id="element-13855">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13856">
                    {t('pages.Connect.authorized_domains')}
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.examplecom_apiexamplecom')} value={newDomains} onChange={e => setNewDomains(e.target.value)} data-id="element-13857" />

                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-13858">
                    {t('pages.Connect.commaseparated_list_of_domains')}
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-13859">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13860">
                    {t('pages.Connect.scopes')}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-13861">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${newScopes.includes(id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-13862">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${newScopes.includes(id) ? 'bg-[#E2231A] border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13863">
                          {newScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-13864" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-13865">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-13866">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-13867">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-13868">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={handleAddApp} disabled={!newName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${newName.trim() ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-13869">
                  {t('pages.Connect.create_connection')}
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── TOGGLE CONFIRMATION MODAL ─── */}
      {pendingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setPendingToggle(null);
    }} data-id="element-13870">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-13871">
              <div className="p-6 text-center" data-id="element-13872">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${pendingApp.status === 'active' ? 'bg-red-50' : 'bg-green-50'}`} data-id="element-13873">
                  {pendingApp.status === 'active' ? <XCircleIcon size={24} className="text-red-500" data-id="element-13874" /> : <CheckCircleIcon size={24} className="text-green-500" data-id="element-13875" />}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-13876">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}{' '}
                  {pendingApp.name}?
                </h3>
                <p className="text-sm text-gray-500" data-id="element-13877">
                  {pendingApp.status === 'active' ? 'This will revoke API access for this connection. All active sessions will be terminated.' : 'This will restore API access for this connection and allow new transactions.'}
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-13878">
                <button onClick={() => setPendingToggle(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-13879">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={confirmToggle} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${pendingApp.status === 'active' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`} data-id="element-13880">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── EDIT CONNECTION MODAL ─── */}
      {editingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setEditingApp(null);
    }} data-id="element-13881">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-13882">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-13883">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-13884">
                  {t('pages.Connect.edit_connection')}
                </h3>
                <button onClick={() => setEditingApp(null)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-13885">
                  <XIcon size={18} aria-hidden="true" data-id="element-13886" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-13887">
                {/* App name */}
                <div data-id="element-13888">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13889">
                    {t('pages.Connect.app_name')}{' '}
                    <span className="text-red-400" data-id="element-13890">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.eg_opera_pms_salesforce')} value={editName} onChange={e => setEditName(e.target.value)} autoFocus data-id="element-13891" />
                </div>

                {/* Domains */}
                <div data-id="element-13892">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13893">
                    {t('pages.Connect.authorized_domains')}
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.examplecom_apiexamplecom')} value={editDomains} onChange={e => setEditDomains(e.target.value)} data-id="element-13894" />

                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-13895">
                    {t('pages.Connect.commaseparated_list_of_domains')}
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-13896">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13897">
                    {t('pages.Connect.scopes')}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-13898">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleEditScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${editScopes.includes(id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-13899">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${editScopes.includes(id) ? 'bg-[#E2231A] border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13900">
                          {editScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-13901" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-13902">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-13903">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0" data-id="element-13904">
                <button onClick={() => setEditingApp(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-13905">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={handleSaveEdit} disabled={!editName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${editName.trim() ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-13906">
                  {t('pages.Connect.save_changes')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}